import type { DocuSignTemplate, DocuSignTextTab } from '../types';
import type {
  DocumensoPayload,
  DocumensoRecipient,
  DocumensoField,
  ConversionResult,
  PixelCoordinates,
  PercentageCoordinates,
  PDFDimensions,
} from '../types/documenso';

/**
 * Convert DocuSign pixel coordinates to Documenso percentage coordinates
 */
function convertPixelsToPercentage(
  pixels: PixelCoordinates,
  pdfDimensions: PDFDimensions
): PercentageCoordinates {
  const positionX = (pixels.x / pdfDimensions.width) * 100;
  const positionY = (pixels.y / pdfDimensions.height) * 100;
  const width = (pixels.width / pdfDimensions.width) * 100;
  const height = (pixels.height / pdfDimensions.height) * 100;

  // Ensure values are in valid range [0, 100]
  return {
    positionX: Math.max(0, Math.min(100, positionX)),
    positionY: Math.max(0, Math.min(100, positionY)),
    width: Math.max(0, Math.min(100, width)),
    height: Math.max(0, Math.min(100, height)),
  };
}

/**
 * Placeholder label overrides for unlocked fields
 */
const PLACEHOLDER_OVERRIDES: Record<string, string> = {
  'a_zip': 'PLZ',
  'a_street': 'Straße + Hausnummer',
  'a_city': 'Ort',
  'v_zip': 'PLZ',
  'v_street': 'Straße + Hausnummer',
  'v_city': 'Ort',
};

/**
 * Replacement variable from event data
 */
export interface ReplacementVariable {
  name: string;
  content: string | boolean;
}

/**
 * Event data structure for extracting signer information
 */
export interface EventData {
  ZZVLeadObject?: {
    antragsteller_person?: {
      a_email?: string;
      a_firstname?: string;
      a_lastname?: string;
    };
  };
  replacementVariables?: ReplacementVariable[];
}

/**
 * Convert a DocuSign radio group to Documenso FLEXIBLE_RADIO field
 * - If all radios are locked: create a single locked TEXT field with "X" at the selected radio's position
 * - If not locked: convert to a FLEXIBLE_RADIO field with individually positioned radio buttons
 */
function convertRadioGroupToFields(
  radioGroup: any,
  pdfDimensions: PDFDimensions,
  fileIdentifier: number = 0,
  recipientId: number = 0
): DocumensoField[] {
  const fields: DocumensoField[] = [];
  
  if (!radioGroup.radios || !Array.isArray(radioGroup.radios)) {
    return fields;
  }

  // Check if all radios are locked
  const allLocked = radioGroup.radios.every((radio: any) => radio.locked === 'true');
  
  if (allLocked) {
    // Find the selected radio button
    const selectedRadio = radioGroup.radios.find((radio: any) => radio.selected === 'true');
    
    if (selectedRadio) {
      // Create a single locked TEXT field with "X" at the selected radio's position
      const x = parseFloat(selectedRadio.xPosition);
      const y = parseFloat(selectedRadio.yPosition);
      const page = parseInt(selectedRadio.pageNumber, 10);
      
      // Use a small square size for the "X"
      const size = 12; // Small square
      
      if (!isNaN(x) && !isNaN(y) && !isNaN(page) && page >= 1) {
        const percentageCoords = convertPixelsToPercentage(
          { x, y, width: size, height: size },
          pdfDimensions
        );
        
        fields.push({
          id: fileIdentifier,
          type: 'TEXT',
          recipientId,
          page,
          positionX: percentageCoords.positionX,
          positionY: percentageCoords.positionY,
          width: percentageCoords.width,
          height: percentageCoords.height,
          fieldMeta: {
            type: 'text',
            label: `${radioGroup.groupName} (locked)`,
            text: 'X',
            readOnly: true,
            required: false,
          },
        });
      }
    }
  } else {
    // Convert to FLEXIBLE_RADIO field with individually positioned radio buttons
    // Use a small square size for radio buttons
    const size = 12;
    
    // Get the first radio's page (all radios should be on the same page)
    const firstRadio = radioGroup.radios[0];
    const page = parseInt(firstRadio.pageNumber, 10);
    
    if (isNaN(page) || page < 1) {
      return fields;
    }
    
    // Find the top-left position (minimum X and Y) across all radios for the container
    let minX = Infinity;
    let minY = Infinity;
    
    for (const radio of radioGroup.radios) {
      const x = parseFloat(radio.xPosition);
      const y = parseFloat(radio.yPosition);
      
      if (!isNaN(x) && !isNaN(y)) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
      }
    }
    
    if (!isFinite(minX) || !isFinite(minY)) {
      return fields;
    }
    
    // Convert container position to percentage
    const containerCoords = convertPixelsToPercentage(
      { x: minX, y: minY, width: size, height: size },
      pdfDimensions
    );
    
    // Convert each radio button to an item with RELATIVE positioning in PIXELS
    const items = radioGroup.radios
      .map((radio: any, index: number) => {
        const x = parseFloat(radio.xPosition);
        const y = parseFloat(radio.yPosition);
        
        if (isNaN(x) || isNaN(y)) {
          return null;
        }
        
        // Calculate relative pixel offset from container (top-left corner)
        const relativePixelX = x - minX;
        const relativePixelY = y - minY;
        
        return {
          id: index + 1,
          value: radio.value || `Option ${index + 1}`,
          checked: radio.selected === 'true',
          positionX: Math.max(0, relativePixelX),  // Relative position in PIXELS
          positionY: Math.max(0, relativePixelY),  // Relative position in PIXELS
          width: size,  // Width in pixels
          height: size, // Height in pixels
        };
      })
      .filter((item: { id: number; value: string; checked: boolean; positionX: number; positionY: number; width: number; height: number } | null): item is { id: number; value: string; checked: boolean; positionX: number; positionY: number; width: number; height: number } => item !== null);
    
    if (items.length > 0) {
      fields.push({
        id: fileIdentifier,
        type: 'FLEXIBLE_RADIO',
        recipientId,
        page,
        positionX: containerCoords.positionX,
        positionY: containerCoords.positionY,
        width: containerCoords.width,
        height: containerCoords.height,
        fieldMeta: {
          type: 'flexible-radio',
          groupName: radioGroup.groupName || 'radio-group',
          required: radioGroup.templateRequired === 'true' || radioGroup.radios.some((r: any) => r.required === 'true'),
          items,
        },
      });
    }
  }
  
  return fields;
}

/**
 * Convert a DocuSign text tab to a Documenso field
 */
function convertTextTabToField(
  tab: DocuSignTextTab,
  pdfDimensions: PDFDimensions,
  fileIdentifier: number = 0,
  recipientId: number = 0,
  replacementMap?: Map<string, string>
): DocumensoField | null {
  try {
    // Parse coordinate values (DocuSign stores as strings)
    const x = parseFloat(tab.xPosition);
    const y = parseFloat(tab.yPosition);
    const width = parseFloat(tab.width || '100');
    const height = parseFloat(tab.height || '20');
    const page = parseInt(tab.pageNumber, 10);

    // Validate numeric values
    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height) || isNaN(page)) {
      console.warn(`Invalid coordinate values for field: ${tab.tabLabel}`);
      return null;
    }

    // Validate non-negative
    if (x < 0 || y < 0 || width < 0 || height < 0 || page < 1) {
      console.warn(`Negative coordinate values for field: ${tab.tabLabel}`);
      return null;
    }

    // Convert coordinates
    const percentageCoords = convertPixelsToPercentage(
      { x, y, width, height },
      pdfDimensions
    );

    // Check if this is a replacement variable (starts with \* or just matches a variable name)
    const isReplacementVar = tab.tabLabel?.startsWith('\\*');
    const fieldLabel = isReplacementVar 
      ? tab.tabLabel.substring(2) // Remove \* prefix
      : tab.tabLabel || 'Text Field';

    // For replacement variables, check if we have a value from event data
    // Also check if the field name (without prefix) matches a replacement variable
    const replacementValue = replacementMap?.get(fieldLabel);
    
    // Determine the field value
    let fieldValue: string | undefined;
    
    if (isReplacementVar || replacementValue !== undefined) {
      // This is a replacement variable - use value from event data
      fieldValue = replacementValue;
    } else if (tab.value) {
      // Regular field with static value
      fieldValue = tab.value;
    }

    // Build field metadata
    // For unlocked fields with override, use the override as the label
    const displayLabel = (tab.locked !== 'true' && PLACEHOLDER_OVERRIDES[fieldLabel]) 
      ? PLACEHOLDER_OVERRIDES[fieldLabel] 
      : fieldLabel;
    
    const fieldMeta: DocumensoField['fieldMeta'] = {
      type: 'text',
      label: displayLabel,
      required: tab.required === 'true',
      readOnly: tab.locked === 'true',
    };

    // Add placeholder for unlocked fields if override exists
    if (tab.locked !== 'true' && PLACEHOLDER_OVERRIDES[fieldLabel]) {
      fieldMeta.placeholder = PLACEHOLDER_OVERRIDES[fieldLabel];
    }

    // Set the field value
    if (fieldValue !== undefined) {
      fieldMeta.text = fieldValue;
    } else if (tab.locked === 'true') {
      // Locked fields MUST have text (Documenso requirement)
      // Use a space to satisfy the requirement
      fieldMeta.text = ' ';
    } else {
      // Unlocked fields without value: set to empty string
      fieldMeta.text = '';
    }

    // Set verticalAlign to top for locked multiline fields
    if (tab.locked === 'true' && fieldMeta.text && fieldMeta.text.includes('\n')) {
      fieldMeta.verticalAlign = 'top';
    }

    return {
      id: fileIdentifier,
      type: 'TEXT',
      recipientId,
      page,
      positionX: percentageCoords.positionX,
      positionY: percentageCoords.positionY,
      width: percentageCoords.width,
      height: percentageCoords.height,
      fieldMeta,
    };
  } catch (error) {
    console.error(`Error converting field ${tab.tabLabel}:`, error);
    return null;
  }
}

/**
 * Convert a DocuSign dateSignedTab to a Documenso DATE field
 */
function convertDateSignedTabToField(
  tab: any,
  pdfDimensions: PDFDimensions,
  fileIdentifier: number = 0,
  recipientId: number = 0
): DocumensoField | null {
  try {
    const x = parseFloat(tab.xPosition);
    const y = parseFloat(tab.yPosition);
    const rawWidth = parseFloat(tab.width || '0');
    const rawHeight = parseFloat(tab.height || '0');
    // DocuSign dateSignedTabs often have width/height of 0; use sensible defaults
    const width = rawWidth > 0 ? rawWidth : 100;
    const height = rawHeight > 0 ? rawHeight : 20;
    const page = parseInt(tab.pageNumber, 10);

    if (isNaN(x) || isNaN(y) || isNaN(page)) {
      console.warn(`Invalid coordinate values for date signed field: ${tab.tabLabel}`);
      return null;
    }

    if (x < 0 || y < 0 || page < 1) {
      console.warn(`Negative coordinate values for date signed field: ${tab.tabLabel}`);
      return null;
    }

    const percentageCoords = convertPixelsToPercentage(
      { x, y, width, height },
      pdfDimensions
    );

    // Date-signed fields auto-fill with the current date when the signer
    // clicks them. They must NOT be readOnly (that would require a
    // pre-filled value). Set required so the signer cannot skip it.
    return {
      id: fileIdentifier,
      type: 'DATE',
      recipientId,
      page,
      positionX: percentageCoords.positionX,
      positionY: percentageCoords.positionY,
      width: percentageCoords.width,
      height: percentageCoords.height,
      fieldMeta: {
        type: 'date',
        label: "Datum", //tab.tabLabel || 'Date Signed',
        readOnly: false,
        required: true,
      },
    };
  } catch (error) {
    console.error(`Error converting date signed field ${tab.tabLabel}:`, error);
    return null;
  }
}

/**
 * Convert a DocuSign signature tab to a Documenso SIGNATURE field
 */
function convertSignatureTabToField(
  tab: any,
  pdfDimensions: PDFDimensions,
  fileIdentifier: number = 0,
  recipientId: number = 0
): DocumensoField | null {
  try {
    // Parse coordinate values
    const x = parseFloat(tab.xPosition);
    const y = parseFloat(tab.yPosition) + 10; // Position 10px lower
    const width = parseFloat(tab.width || '100');
    const height = parseFloat(tab.height || '20');
    const page = parseInt(tab.pageNumber, 10);

    // Validate numeric values
    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height) || isNaN(page)) {
      console.warn(`Invalid coordinate values for signature field: ${tab.tabLabel}`);
      return null;
    }

    // Validate non-negative
    if (x < 0 || y < 0 || width < 0 || height < 0 || page < 1) {
      console.warn(`Negative coordinate values for signature field: ${tab.tabLabel}`);
      return null;
    }

    // Convert coordinates
    const percentageCoords = convertPixelsToPercentage(
      { x, y, width, height },
      pdfDimensions
    );

    return {
      id: fileIdentifier,
      type: 'SIGNATURE',
      recipientId,
      page,
      positionX: percentageCoords.positionX,
      positionY: percentageCoords.positionY,
      width: percentageCoords.width,
      height: percentageCoords.height,
    };
  } catch (error) {
    console.error(`Error converting signature field ${tab.tabLabel}:`, error);
    return null;
  }
}

/**
 * Extract recipient from DocuSign template with fields
 */
function extractRecipient(
  template: DocuSignTemplate,
  pdfDimensions: PDFDimensions,
  recipientId: number = 0,
  replacementMap?: Map<string, string>,
  eventData?: EventData
): DocumensoRecipient {
  const signer = template.recipients?.signers?.[0];
  
  const fields: DocumensoField[] = [];
  
  // Convert all signature tabs to fields
  const signatureTabs = signer?.tabs?.signHereTabs || [];
  const signatureFields = signatureTabs
    .map((tab) => convertSignatureTabToField(tab, pdfDimensions, 0, recipientId))
    .filter((field): field is DocumensoField => field !== null);
  fields.push(...signatureFields);
  
  // Convert all dateSignedTabs to DATE fields
  const dateSignedTabs = signer?.tabs?.dateSignedTabs || [];
  const dateFields = dateSignedTabs
    .map((tab: any) => convertDateSignedTabToField(tab, pdfDimensions, 0, recipientId))
    .filter((field: DocumensoField | null): field is DocumensoField => field !== null);
  fields.push(...dateFields);
  
  // Convert all text tabs to fields for this recipient
  const textTabs = signer?.tabs?.textTabs || [];
  const textFields = textTabs
    .map((tab) => convertTextTabToField(tab, pdfDimensions, 0, recipientId, replacementMap))
    .filter((field): field is DocumensoField => field !== null);
  fields.push(...textFields);
  
  // Convert all radio groups to fields
  const radioGroupTabs = signer?.tabs?.radioGroupTabs || [];
  radioGroupTabs.forEach((radioGroup: any) => {
    const radioFields = convertRadioGroupToFields(radioGroup, pdfDimensions, 0, recipientId);
    fields.push(...radioFields);
  });
  
  // Extract email and name from event data if available
  let email = signer?.email || 'signer@example.com';
  let name = signer?.name || 'Signer';
  
  if (eventData?.ZZVLeadObject?.antragsteller_person) {
    const person = eventData.ZZVLeadObject.antragsteller_person;
    
    // Use email from event data
    if (person.a_email) {
      email = person.a_email;
    }
    
    // Construct name from firstname and lastname
    if (person.a_firstname && person.a_lastname) {
      name = `${person.a_firstname} ${person.a_lastname}`;
    } else if (person.a_firstname) {
      name = person.a_firstname;
    } else if (person.a_lastname) {
      name = person.a_lastname;
    }
  }
  
  return {
    email,
    name,
    role: 'SIGNER',
    fields: fields.length > 0 ? fields : undefined,
  };
}

/**
 * Convert DocuSign template to Documenso payload format
 */
export function convertDocuSignToDocumenso(
  template: DocuSignTemplate,
  pdfDimensions: PDFDimensions,
  replacementVariables?: ReplacementVariable[],
  eventData?: EventData
): ConversionResult {
  try {
    // Validate required fields
    if (!template.name) {
      return {
        success: false,
        error: 'Template is missing required field: name',
      };
    }

    if (!template.recipients?.signers || template.recipients.signers.length === 0) {
      return {
        success: false,
        error: 'Template has no recipients defined',
      };
    }

    if (!template.documents || template.documents.length === 0) {
      return {
        success: false,
        error: 'Template has no documents defined',
      };
    }

    // Validate PDF dimensions
    if (!pdfDimensions || pdfDimensions.width <= 0 || pdfDimensions.height <= 0) {
      return {
        success: false,
        error: 'Invalid PDF dimensions: width and height must be positive',
      };
    }

    // Build replacement map from variables
    const replacementMap = new Map<string, string>();
    if (replacementVariables) {
      for (const variable of replacementVariables) {
        // Convert false to empty string, otherwise use content as string
        const value = variable.content === false ? '' : String(variable.content);
        replacementMap.set(variable.name, value);
      }
    }

    // Extract recipient with fields
    const recipient = extractRecipient(template, pdfDimensions, 0, replacementMap, eventData);

    // Build Documenso payload
    const payload: DocumensoPayload = {
      type: 'DOCUMENT',
      title: template.name,
      externalId: template.templateId,
      recipients: [recipient],
      meta: {
        subject: '',
        message: '',
        dateFormat: 'dd.MM.yyyy',
      },
    };

    return {
      success: true,
      payload,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown conversion error',
    };
  }
}
